/* CNC Electric — Mode A: cart-hesitation recovery
 *
 * Fires when a customer has something in the cart and stalls. Claude decides
 * whether to speak at all and what to open with; this file only gathers
 * context, enforces the guards, and measures the result.
 *
 * Deliberate design points:
 *  - Claude may return engage:false. Silence is a valid, common outcome.
 *  - Price and stock are read live from Shopify at decision time. The catalog
 *    JSON is never trusted for either — both move, and both are unforgiving.
 *  - Never engages about an out-of-stock item. 22% of orders are already
 *    cancelled, concentrated on zero-stock SKUs; converting someone onto one
 *    turns a hesitant buyer into a let-down one.
 *  - 80/20 split with a held-out control. Success is NOT checkout clicks: on a
 *    store where 22% of orders are later cancelled, optimising for checkouts
 *    rewards pushing carts onto empty shelves. We log the Shopify cart_token at
 *    engage time so an engaged cart can be joined to the order it became, and
 *    then to whether that order survived. Two metrics, deliberately not merged:
 *      order_placed          - leading, known in minutes, used to tune
 *      delivered_not_cancelled - lagging truth, known in 7-10 days, used to judge
 *
 * Facts here are verified against the live store (2026-08-28):
 *   flat Rs 300 delivery, no free-shipping threshold, 7-10 days, COD available.
 * Do not edit those without re-checking Shopify → Settings → Shipping.
 */
(function () {
  'use strict';

  var CFG = {
    idleMs: 35000,                 // tuned from real idle_start data, not guessed
    cartPageIdleMs: 20000,         // already on /cart = higher intent, engage sooner
    proxy: 'https://cspl-erp.pages.dev/api/claude/v1/messages',
    model: 'claude-haiku-4-5-20251001',
    rtdb: 'https://cs-daily-reports-default-rtdb.asia-southeast1.firebasedatabase.app/cnc_chatbot/events.json',
    whatsapp: '923261111376',
    ship: { flat: 300, freeThreshold: null, daysText: '7 to 10 days' },
    treatmentShare: 0.8,   // 80/20. A held-out control is the only way to know the bot caused anything.
    maxCartItemsInContext: 6
  };

  var SEEN_KEY = 'cnc_cr_fired';
  var ARM_KEY = 'cnc_cr_arm';

  function sid() {
    try {
      var k = 'cnc_cr_sid', v = sessionStorage.getItem(k);
      if (!v) { v = 'cr' + Date.now() + Math.random().toString(36).slice(2, 6); sessionStorage.setItem(k, v); }
      return v;
    } catch (e) { return 'cr' + Date.now(); }
  }

  function log(type, data) {
    try {
      fetch(CFG.rtdb, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: type, ts: Date.now(), sid: sid(), data: data || {} })
      }).catch(function () {});
    } catch (e) {}
  }

  /* Deterministic split so a customer stays in one arm for the whole session. */
  function arm() {
    try {
      var a = sessionStorage.getItem(ARM_KEY);
      if (a) return a;
      var s = sid(), h = 0;
      for (var i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      a = (Math.abs(h) % 100) / 100 < CFG.treatmentShare ? 'treatment' : 'control';
      sessionStorage.setItem(ARM_KEY, a);
      return a;
    } catch (e) { return 'control'; }
  }

  function fired() { try { return sessionStorage.getItem(SEEN_KEY) === '1'; } catch (e) { return false; } }
  function markFired() { try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (e) {} }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* Live price and availability, straight from Shopify. Never from the catalog. */
  function stockFor(handle, variantId) {
    if (!handle) return Promise.resolve(null);
    return getJSON('/products/' + handle + '.js').then(function (p) {
      if (!p) return null;
      var v = null, i;
      for (i = 0; i < (p.variants || []).length; i++) {
        if (String(p.variants[i].id) === String(variantId)) { v = p.variants[i]; break; }
      }
      return {
        productAvailable: !!p.available,
        variantAvailable: v ? !!v.available : null,
        livePrice: v ? v.price / 100 : null,
        variantTitle: v ? v.title : null,
        anyVariantAvailable: (p.variants || []).some(function (x) { return x.available; })
      };
    });
  }

  function pageContext() {
    var path = location.pathname;
    return {
      path: path,
      type: /^\/products\//.test(path) ? 'product'
        : /^\/collections\//.test(path) ? 'collection'
        : path === '/cart' ? 'cart'
        : path === '/' ? 'home' : 'other',
      title: document.title.slice(0, 90),
      referrer: (function () { try { return document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) { return ''; } })(),
      device: window.innerWidth <= 768 ? 'mobile' : 'desktop'
    };
  }

  function buildContext(cart, stocks, trigger) {
    var items = (cart.items || []).slice(0, CFG.maxCartItemsInContext).map(function (it, i) {
      var s = stocks[i] || {};
      return {
        title: it.product_title,
        variant: it.variant_title || null,
        qty: it.quantity,
        line_price_pkr: it.line_price / 100,
        handle: it.handle,
        live_price_pkr: s.livePrice,
        in_stock: s.variantAvailable,
        other_variants_in_stock: s.anyVariantAvailable
      };
    });
    var total = cart.total_price / 100;
    return {
      trigger: trigger,
      cart: {
        items: items,
        item_count: cart.item_count,
        total_pkr: total,
        shipping_pkr: CFG.ship.flat,
        shipping_as_pct_of_cart: total ? Math.round((CFG.ship.flat / total) * 100) : null,
        any_item_out_of_stock: items.some(function (x) { return x.in_stock === false; })
      },
      page: pageContext(),
      time_pkt: new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Karachi' })
    };
  }

  var SYSTEM = [
    'You are the CNC Electric advisor on cncelectric.pk, a Pakistani electrical-protection retailer.',
    'A customer has items in their cart and has stalled. Decide whether to speak, and if so, what to open with.',
    '',
    'YOUR JOB: remove the one specific doubt holding them back. You are closing a considered,',
    'trust-heavy purchase, not selling. The product is already chosen.',
    '',
    'DECIDE NOT TO ENGAGE when: the cart looks like a mis-click, the customer is clearly still',
    'browsing with intent, or you have nothing specific and useful to say. Silence is better than',
    'a generic "can I help?". Returning engage:false is a normal, good outcome.',
    '',
    'HARD RULES — breaking any of these is worse than not engaging:',
    '1. STOCK. If any cart item has in_stock false, never encourage checkout on it. Say plainly that',
    '   the item needs a stock check and offer WhatsApp. Never imply something is available.',
    '2. PRICE. Only ever use live_price_pkr from the context. Never recall or estimate a price.',
    '3. DELIVERY — these are the verified facts, do not improve on them:',
    '   flat Rs 300 delivery on every order; there is NO free-delivery threshold;',
    '   allow 7 to 10 days; Cash on Delivery is available.',
    '   Never promise free delivery. Never promise faster. A promise that breaks at checkout',
    '   destroys more trust than the sale is worth.',
    '4. NO DISCOUNTS. You have no authority to offer any discount, voucher or price match.',
    '5. Never name or disparage competitors or other retailers.',
    '6. Never invent products, models, specs or stock. If unsure, hand off to WhatsApp +92 326-1111376.',
    '',
    'TRUE THINGS YOU MAY USE: 5-year replacement warranty on breakers, no questions asked.',
    'Cash on Delivery. Official distributor, genuine stock, free technical help on WhatsApp.',
    '',
    'TONE: plain conversational text, like a real salesperson on WhatsApp. No markdown, no bullets,',
    'no emojis, no headings. Mirror the customer language if you can infer it; default to simple',
    'English with Roman Urdu warmth. Two sentences at most in the opener. Lead with the likely doubt,',
    'never with a greeting alone.',
    '',
    'THE LIKELY DOUBTS, in rough order: is it genuine; is it the right spec for my setup;',
    'is it worth the price; is COD real and safe; how long is delivery; what if it fails.',
    'Small carts feel the Rs 300 delivery most sharply — look at shipping_as_pct_of_cart.',
    '',
    'Reply with ONLY a JSON object, no prose around it:',
    '{"engage": true|false, "opener": "<two sentences max, empty if not engaging>",',
    ' "doubt": "<the doubt you are addressing>", "reason": "<why you chose this, one line>",',
    ' "confidence": 0.0-1.0}'
  ].join('\n');

  function askClaude(ctx) {
    return fetch(CFG.proxy, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: CFG.model,
        max_tokens: 400,
        system: SYSTEM,
        messages: [{ role: 'user', content: JSON.stringify(ctx, null, 1) }]
      })
    }).then(function (r) {
      if (!r.ok) return r.text().then(function (t) { throw new Error(r.status + ': ' + t.slice(0, 120)); });
      return r.json();
    }).then(function (d) {
      var text = (d.content && d.content[0] && d.content[0].text) || '';
      var m = text.match(/\{[\s\S]*\}/);
      if (!m) throw new Error('no JSON in reply');
      return JSON.parse(m[0]);
    });
  }

  /* Open the existing widget and place the opener as a bot message. */
  function engage(opener) {
    var fab = document.getElementById('cnc-fab');
    var panel = document.getElementById('cnc-panel');
    if (!fab || !panel) return false;
    if (getComputedStyle(panel).display === 'none') fab.click();

    setTimeout(function () {
      if (window.CNC_BOT && typeof window.CNC_BOT.addBot === 'function') {
        window.CNC_BOT.addBot(opener);            // keeps the bot's own history in sync
      } else {
        var msgs = document.getElementById('cnc-msgs');
        if (!msgs) return;
        var d = document.createElement('div');
        d.className = 'cnc-msg cnc-bot';
        d.textContent = opener;
        msgs.appendChild(d);
        msgs.scrollTop = msgs.scrollHeight;
      }
    }, 350);
    return true;
  }

  function run(trigger) {
    if (fired()) return;
    markFired();

    getJSON('/cart.js').then(function (cart) {
      if (!cart || !cart.item_count) return;                     // nothing to recover

      var a = arm();
      // cart.token is what Shopify writes onto the order as cart_token — the join key
      log('cr_trigger', {
        trigger: trigger, arm: a, cart_token: cart.token || '',
        cart_total: cart.total_price / 100, items: cart.item_count
      });
      if (a === 'control') return;                               // measured, never spoken to

      var items = (cart.items || []).slice(0, CFG.maxCartItemsInContext);
      return Promise.all(items.map(function (it) { return stockFor(it.handle, it.variant_id); }))
        .then(function (stocks) {
          var ctx = buildContext(cart, stocks, trigger);
          log('cr_context', {
            out_of_stock: ctx.cart.any_item_out_of_stock,
            total: ctx.cart.total_pkr,
            ship_pct: ctx.cart.shipping_as_pct_of_cart
          });
          return askClaude(ctx).then(function (d) {
            log('cr_decision', {
              engage: !!d.engage, doubt: (d.doubt || '').slice(0, 80),
              reason: (d.reason || '').slice(0, 140), confidence: d.confidence
            });
            if (d.engage && d.opener) {
              if (engage(d.opener)) log('cr_engaged', {
                cart_token: cart.token || '', arm: a,
                cart_total: cart.total_price / 100,
                out_of_stock: ctx.cart.any_item_out_of_stock,
                opener: String(d.opener).slice(0, 400)
              });
            }
          });
        });
    }).catch(function (e) { log('cr_error', { message: String(e && e.message || e).slice(0, 200) }); });
  }


  /* ------------------------------------------------------------------
   * Price + stock guard on rendered product cards.
   *
   * The bot writes the price into its own product card, which makes the
   * model the source of a number the customer acts on. An audit on
   * 2026-08-28 found 28 of 54 embedded prices wrong, 25 of them too LOW —
   * quoted cheap, charged more at checkout.
   *
   * This watches for cards as they appear and overwrites the price from
   * Shopify, and flags anything out of stock. The model can be wrong here
   * without the customer ever seeing it.
   * ------------------------------------------------------------------ */
  function fixCard(card) {
    var handle = card.getAttribute('data-handle');
    if (!handle || card.getAttribute('data-price-checked')) return;
    card.setAttribute('data-price-checked', '1');

    getJSON('/products/' + handle + '.js').then(function (p) {
      if (!p || !p.variants || !p.variants.length) return;
      var prices = p.variants.map(function (v) { return v.price / 100; });
      var live = Math.min.apply(null, prices);
      var avail = p.variants.some(function (v) { return v.available; });

      var el = card.querySelector('.cnc-card-price');
      if (el) {
        var shown = (el.textContent || '').replace(/[^\d]/g, '');
        var liveText = live.toLocaleString('en-PK');   // the card template already prints 'Rs.'
        if (String(live) !== shown) {
          el.textContent = liveText;
          log('cr_price_corrected', { handle: handle, shown: shown, live: live });
        }
      }

      if (!avail) {
        if (!card.querySelector('.cnc-oos')) {
          var w = document.createElement('div');
          w.className = 'cnc-oos';
          w.style.cssText = 'font-size:12px;color:#b91c1c;font-weight:600;margin-top:4px';
          w.textContent = 'Out of stock — message us on WhatsApp to confirm availability';
          (card.querySelector('.cnc-card-body') || card).appendChild(w);
        }
        var atc = card.querySelector('.cnc-atc');
        if (atc) { atc.disabled = true; atc.style.opacity = '.45'; atc.textContent = 'Out of stock'; }
        log('cr_oos_card', { handle: handle });
      }

      var wa = card.querySelector('.cnc-wa');
      if (wa && wa.href) wa.href = wa.href.replace(/Rs\.?%20?[\d,]+/, 'Rs.' + live);
    });
  }

  function watchCards() {
    var root = document.getElementById('cnc-msgs');
    if (!root) return setTimeout(watchCards, 1500);
    Array.prototype.forEach.call(root.querySelectorAll('.cnc-card[data-handle]'), fixCard);
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches('.cnc-card[data-handle]')) fixCard(n);
          if (n.querySelectorAll) Array.prototype.forEach.call(n.querySelectorAll('.cnc-card[data-handle]'), fixCard);
        });
      });
    }).observe(root, { childList: true, subtree: true });
  }
  watchCards();

  /* ---- triggers ---- */
  var idleTimer = null;
  function resetIdle() {
    clearTimeout(idleTimer);
    var wait = location.pathname === '/cart' ? CFG.cartPageIdleMs : CFG.idleMs;
    idleTimer = setTimeout(function () { run(location.pathname === '/cart' ? 'cart_page_idle' : 'idle'); }, wait);
  }
  ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(function (ev) {
    window.addEventListener(ev, resetIdle, { passive: true });
  });

  document.addEventListener('mouseout', function (e) {
    if (!e.relatedTarget && e.clientY <= 0) run('exit_intent');
  });

  /* A checkout click is an intermediate step, NOT success. The real outcome is
     an order that is placed and then not cancelled, resolved later by joining
     cart_token to Shopify. Logged only so the funnel has its middle. */
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest &&
      e.target.closest('[name="checkout"], a[href*="/checkout"], .cart__checkout, #checkout');
    if (!t) return;
    getJSON('/cart.js').then(function (c) {
      log('cr_checkout_click', {
        arm: arm(), fired: fired(),
        cart_token: (c && c.token) || '',
        cart_total: c ? c.total_price / 100 : null
      });
    });
  }, true);

  /* Only start once a cart exists — no point watching an empty session. */
  getJSON('/cart.js').then(function (c) { if (c && c.item_count) resetIdle(); });
  document.addEventListener('cnc:cart-updated', resetIdle);
})();
